// YouTube IFrame Player adapter — isolates the YT player API behind a shared runtime boundary.
// The practice UI depends only on PracticeRuntimeAdapter; it never reaches the YT global directly.

export interface PracticeRuntimeAdapter {
  play(): void;
  pause(): void;
  seek(time: number): void;
  mute(muted: boolean): void;
  /** Returns ok:false with the reason when the rate is not accepted by the underlying player. */
  setSpeed(rate: number): SetSpeedResult;
  getDuration(): number;
  getCurrentTime(): number;
  isReady(): boolean;
  hasEnded(): boolean;
  /** True while the player is loading (buffering) rather than playing or blocked. */
  isBuffering(): boolean;
}

export type SetSpeedResult =
  | { ok: true; appliedRate: number }
  | { ok: false; reason: 'unsupported-rate'; requestedRate: number };

// Rates that the YouTube IFrame API actually accepts.
// 0.8, 0.9, 1.1, 1.2 (present in SPEED_OPTIONS) are NOT in this set.
export const YOUTUBE_SUPPORTED_RATES: ReadonlySet<number> = new Set([
  0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2,
]);

// YT.PlayerState values from the IFrame API.
export const YT_STATE = {
  UNSTARTED: -1,
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2,
  BUFFERING: 3,
  CUED: 5,
} as const;

/**
 * Minimal shape of the YouTube IFrame player we depend on.
 * Kept narrow so tests can easily stub it.
 */
export interface YTPlayerLike {
  playVideo(): void;
  pauseVideo(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  mute(): void;
  unMute(): void;
  setPlaybackRate(suggestedRate: number): void;
  getDuration(): number;
  getCurrentTime(): number;
  getPlayerState(): number;
}

/**
 * Wraps a YTPlayerLike instance with the PracticeRuntimeAdapter interface.
 * Mirror mode is NOT applied here — the UI layer owns it via CSS transform.
 */
export function createYouTubeAdapter(player: YTPlayerLike): PracticeRuntimeAdapter {
  return {
    play() {
      player.playVideo();
    },

    pause() {
      player.pauseVideo();
    },

    seek(time: number) {
      player.seekTo(time, true);
    },

    mute(muted: boolean) {
      if (muted) {
        player.mute();
      } else {
        player.unMute();
      }
    },

    setSpeed(rate: number): SetSpeedResult {
      if (!YOUTUBE_SUPPORTED_RATES.has(rate)) {
        return { ok: false, reason: 'unsupported-rate', requestedRate: rate };
      }
      player.setPlaybackRate(rate);
      return { ok: true, appliedRate: rate };
    },

    getDuration() {
      return player.getDuration();
    },

    getCurrentTime() {
      return player.getCurrentTime();
    },

    isReady() {
      const state = player.getPlayerState();
      return state !== YT_STATE.UNSTARTED;
    },

    hasEnded() {
      return player.getPlayerState() === YT_STATE.ENDED;
    },

    isBuffering() {
      return player.getPlayerState() === YT_STATE.BUFFERING;
    },
  };
}
